import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '../../../../i18n';
import {
  buildQuickMathTokens,
  quickMathVisibleTokenCount,
  quickMathMaxRevealStep,
} from './quickMathTokens';
import './quick-math.css';

/** First pause after the initial `n0 op n1` (was 2s; +1s baseline). */
const HOST_REVEAL_INITIAL_MS = 3000;
/** Each further op+number waits this much longer than the previous pause. */
const HOST_REVEAL_ADD_PER_CHUNK_MS = 1000;

function delayMsBeforeAdvance(currentStep: number): number {
  return HOST_REVEAL_INITIAL_MS + HOST_REVEAL_ADD_PER_CHUNK_MS * currentStep;
}

interface QuickMathHostProgressiveProps {
  numbers: number[];
  ops: ('+' | '-')[];
  roundEnded: boolean;
  questionId: string;
}

/**
 * Host-only: show n0 + n1 first, then slide in each next op + number from the right.
 * Pauses grow by +1s each chunk (3s, 4s, 5s, … after the initial triple).
 */
export const QuickMathHostProgressive: React.FC<QuickMathHostProgressiveProps> = ({
  numbers,
  ops,
  roundEnded,
  questionId,
}) => {
  const { t } = useTranslation();
  const tokens = useMemo(() => buildQuickMathTokens(numbers, ops), [numbers, ops]);
  const maxStep = useMemo(() => quickMathMaxRevealStep(tokens.length), [tokens.length]);

  const [step, setStep] = useState(0);

  useEffect(() => {
    setStep(0);
  }, [questionId]);

  useEffect(() => {
    if (roundEnded || tokens.length <= 3 || maxStep <= 0) return;

    let cancelled = false;
    let localStep = 0;
    let timeoutId: number | undefined;

    const scheduleNext = () => {
      if (cancelled || localStep >= maxStep) return;
      const delay = delayMsBeforeAdvance(localStep);
      timeoutId = window.setTimeout(() => {
        if (cancelled) return;
        localStep += 1;
        setStep(localStep);
        if (localStep < maxStep) scheduleNext();
      }, delay);
    };

    scheduleNext();

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [questionId, roundEnded, tokens.length, maxStep]);

  const visibleCount = quickMathVisibleTokenCount(step, tokens.length);
  const visible = tokens.slice(0, visibleCount);

  return (
    <div className="quick-math-host-progressive-wrap">
      <div className="quick-math-host-progressive" role="img" aria-label={t('bonus.player.quickMathHostExpressionAria')}>
        {visible.map(({ key, text }, idx) => {
          const isNewestPair = idx >= 3 && idx >= visibleCount - 2;
          return (
            <span
              key={isNewestPair ? `${key}-s${step}` : key}
              className={
                isNewestPair
                  ? 'quick-math-host-token quick-math-host-token--slide-in'
                  : 'quick-math-host-token'
              }
            >
              {text}
            </span>
          );
        })}
      </div>
    </div>
  );
};
